import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, Environment } from '@react-three/drei';
import { Suspense, useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useConfiguratorStore } from '@store/useConfiguratorStore';
import ModelLoader from './ModelLoader';
import ModelLoadingBar from '../ModelLoadingBar';
import './Scene.css';
import * as THREE from 'three';

// Extend Window interface for global functions
declare global {
    interface Window {
        _sceneReset?: () => void;
        _sceneDownload?: () => void;
        _sceneZoomIn?: () => void;
        _sceneZoomOut?: () => void;
        _sceneRecordVideo?: () => void;
        _sceneCaptureMultiAngle?: (faceCount: number, options?: { mode?: 'psd' | 'proof', isWireframe?: boolean, isArtworkOnly?: boolean }) => Promise<HTMLCanvasElement[]>;
    }
}

// Camera controls component with reset functionality
const CameraControls = forwardRef<any>((_, ref) => {
    const controlsRef = useRef<any>();
    const { camera, gl } = useThree();
    const { toolMode, setZoomCapabilities } = useConfiguratorStore();

    useImperativeHandle(ref, () => ({
        reset: () => {
            if (controlsRef.current) {
                // Reset camera position - Responsive distance
                const isMobile = window.innerWidth < 768;
                const distance = isMobile ? 1.8 : 2.5;
                camera.position.set(0, 0.5, distance);
                
                // Reset controls target
                controlsRef.current.target.set(0, 0, 0);
                controlsRef.current.update();
            }
        },
        download: () => {
            try {
                // Get canvas as data URL
                // Create a temporary canvas to composite the background
                const canvas = gl.domElement;
                const tempCanvas = document.createElement('canvas');
                tempCanvas.width = canvas.width;
                tempCanvas.height = canvas.height;
                const ctx = tempCanvas.getContext('2d');
                
                if (ctx) {
                    // 1. Fill with the Scene Background Color
                    const bg = useConfiguratorStore.getState().sceneBackground;
                    ctx.fillStyle = bg;
                    ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

                    // 2. Draw the 3D Canvas on top
                    ctx.drawImage(canvas, 0, 0);

                    const link = document.createElement('a');
                    link.download = `mockup-${Date.now()}.png`;
                    link.href = tempCanvas.toDataURL('image/png');
                    link.click();
                }
            } catch (error) {
                console.error('Download failed:', error);
            }
        },
        zoomIn: () => {
            if (controlsRef.current) {
                // Smoothly zoom in by reducing camera distance
                const direction = new THREE.Vector3();
                camera.getWorldDirection(direction);
                
                // Calculate target position
                const currentPos = camera.position.clone();
                const target = controlsRef.current.target;
                const step = direction.clone().multiplyScalar(0.5); 
                const newPosition = currentPos.add(step);
                
                // Check distance to target
                const distance = newPosition.distanceTo(target);
                
                if (distance < 0.5) {
                    // Clamp to minDistance (0.5)
                    const clampDir = new THREE.Vector3().subVectors(currentPos, target).normalize();
                    camera.position.copy(target.clone().add(clampDir.multiplyScalar(0.5)));
                } else {
                    camera.position.copy(newPosition);
                }
                controlsRef.current.update();
            }
        },
        zoomOut: () => {
            if (controlsRef.current) {
                // Smoothly zoom out by increasing camera distance
                const direction = new THREE.Vector3();
                camera.getWorldDirection(direction);
                
                // Calculate target position
                const currentPos = camera.position.clone();
                const target = controlsRef.current.target;
                const step = direction.clone().multiplyScalar(0.5);
                const newPosition = currentPos.sub(step);
                
                // Check distance
                const distance = newPosition.distanceTo(target);
                
                if (distance > 10) {
                    // Clamp to maxDistance (10)
                    const clampDir = new THREE.Vector3().subVectors(currentPos, target).normalize();
                    camera.position.copy(target.clone().add(clampDir.multiplyScalar(10)));
                } else {
                    camera.position.copy(newPosition);
                }
                controlsRef.current.update();
            }
        }
    }));

    // Update controls based on tool mode
    const enableRotate = toolMode === 'select' || toolMode === 'rotate' || toolMode === 'zoom' || toolMode === null;
    const enablePan = toolMode === 'pan' || toolMode === 'select' || toolMode === null;
    
    // Configure mouse buttons based on tool mode
    // When pan mode: left mouse = pan, right mouse = pan
    // When select/default: left mouse = rotate, right mouse = pan
    const mouseButtons = toolMode === 'pan' 
        ? {
            LEFT: 2,  // THREE.MOUSE.PAN
            MIDDLE: 1, // THREE.MOUSE.DOLLY
            RIGHT: 2   // THREE.MOUSE.PAN
          }
        : {
            LEFT: 0,   // THREE.MOUSE.ROTATE
            MIDDLE: 1, // THREE.MOUSE.DOLLY
            RIGHT: 2   // THREE.MOUSE.PAN
          }; // Explicitly set defaults to ensure controls re-enable properly

    return (
        <OrbitControls
            ref={controlsRef}
            enablePan={enablePan}
            enableZoom={true}
            enableRotate={enableRotate}
            enableDamping={true}
            dampingFactor={0.05}
            rotateSpeed={0.5}
            minDistance={0.5}
            maxDistance={10}
            onChange={(e) => {
                const ctrl = e?.target as any; 
                if (ctrl) {
                    const distance = ctrl.object.position.distanceTo(ctrl.target);
                    const canIn = distance > 0.51; 
                    const canOut = distance < 9.9;
                    setZoomCapabilities(canIn, canOut);
                }
            }}
            mouseButtons={mouseButtons}
            makeDefault 
        />
    );
});

CameraControls.displayName = 'CameraControls';

// Interface for the exposed camera control methods
interface CameraControlsHandle {
    reset: () => void;
    download: () => void;
    zoomIn: () => void;
    zoomOut: () => void;
    recordVideo: () => void;
    captureMultiAngle: (faceCount: number, options?: { mode?: 'psd' | 'proof', isWireframe?: boolean }) => Promise<HTMLCanvasElement[]>;
}

import { useVideoRecorder } from '@/hooks/useVideoRecorder';

import { availableProducts } from '@/config/products'; // Import product config

// Scene Content Component - Must be inside Canvas to use R3F hooks
const SceneContent = () => {
    const { setIsRecording } = useConfiguratorStore();
    const contentRef = useRef<THREE.Group>(null);
    const { gl, scene, camera } = useThree();

    const { startRecording } = useVideoRecorder({
        onRecordingStart: () => setIsRecording(true),
        onRecordingComplete: (blob) => {
            setIsRecording(false);
            if (window._sceneOnRecordComplete) {
                window._sceneOnRecordComplete(blob);
            }
        }
    });

    // Expose record function
    useEffect(() => {
        if (typeof window !== 'undefined') {
            window._sceneRecordVideo = () => {
                if (contentRef.current) {
                    startRecording(contentRef.current);
                }
            };
        }
    }, [startRecording]);

    // Expose multi-angle capture function for PSD generation
    useEffect(() => {
        if (typeof window === 'undefined') return;
 
        window._sceneCaptureMultiAngle = async (faceCount: number, options?: { mode?: 'psd' | 'proof', isWireframe?: boolean, isArtworkOnly?: boolean }): Promise<HTMLCanvasElement[]> => {
            const mode = options?.mode || 'psd';
            const forceWireframe = options?.isWireframe ?? false;
            const isArtworkOnly = options?.isArtworkOnly ?? false;

            


            // Find the ModelWrapper group
            let modelWrapper: THREE.Object3D | null = null;
            scene.traverse((child) => {
                if (child.name === 'ModelWrapper') {
                    modelWrapper = child;
                }
            });

            if (!modelWrapper) {
                console.error('[PSD] ModelWrapper not found in scene');
                return [];
            }

            const wrapper = modelWrapper as THREE.Object3D;

            // Save original state
            const origQuat = wrapper.quaternion.clone();
            const origCameraPos = camera.position.clone();
            const origBackground = scene.background;
            const origSize = gl.getSize(new THREE.Vector2());
            const origPixelRatio = gl.getPixelRatio();
            
            // Save Camera Projection State
            const cam = camera as THREE.PerspectiveCamera;
            const origFov = cam.fov;
            const origAspect = cam.aspect;
            const origNear = cam.near;
            const origFar = cam.far;

            // Save Store State (Isolate Wireframe Mode)
            const origWireframe = useConfiguratorStore.getState().isWireframeMode;
            if (forceWireframe !== origWireframe) {
                useConfiguratorStore.getState().setWireframeMode(forceWireframe);
                // Wait for effects to settle (Swapping materials takes time)
                await new Promise(resolve => setTimeout(resolve, 600));
            }
 
            // Save Tone Mapping State (Fix for Gray Pens)
            const origToneMapping = gl.toneMapping;
            const origExposure = gl.toneMappingExposure;
            
            // Disable Tone Mapping to ensure Pure White (0xffffff) renders as #FFFFFF
            // This is critical for the Template Proof and PSD background removal
            gl.toneMapping = THREE.NoToneMapping;
            gl.toneMappingExposure = 1.0;
 
            scene.background = null;
            gl.setClearColor(0x000000, 0);
            
            // --- ISOLATE ARTWORK (Hide Hardware) ---
            const hiddenObjects: THREE.Object3D[] = [];
            if (isArtworkOnly) {
                scene.traverse((child: any) => {
                    if (child.isMesh) {
                        const isPrintArea = child.userData.isPrintArea === true || child.name.toLowerCase().includes('printarea');
                        if (!isPrintArea) {
                            if (child.visible) {
                                child.visible = false;
                                hiddenObjects.push(child);
                            }
                        }
                    }
                });
            }


            const captures: HTMLCanvasElement[] = [];
            const angleStep = (2 * Math.PI) / faceCount;
            
            // Capture specific settings
            const isProof = (mode === 'proof');
            
            for (let i = 0; i < faceCount; i++) {
                // PSD (4 angles): Frames 0,1,2 are Standing, Frame 3 is Horizontal.
                // Proof (1 or 4 angles): All frames are Horizontal (Lay Flat).
                const isHorizontalFrame = isProof || (mode === 'psd' && i === faceCount - 1);
                
                // --- ORIENTATION & CAMERA (apply rotation FIRST so we can compute bounds) ---
                const faceRotation = new THREE.Quaternion().setFromAxisAngle(
                    new THREE.Vector3(0, 1, 0),
                    i * angleStep
                );

                if (isProof) {
                    // PROOF/TEMPLATE VIEW: Orthographic & Perfectly Lay Flat
                    const layFlat = new THREE.Quaternion().setFromAxisAngle(
                        new THREE.Vector3(0, 0, 1),
                        -Math.PI * 0.5 // Perfectly horizontal for 2D look
                    );
                    wrapper.quaternion.copy(layFlat.multiply(faceRotation).multiply(origQuat));
                    camera.position.set(0, 0, 50); // Direct front view for Ortho
                    camera.lookAt(0, 0, 0);
                } else if (isHorizontalFrame) {
                    // PSD HORIZONTAL VIEW: Lay the pen on its side with slight tilt
                    const layFlat = new THREE.Quaternion().setFromAxisAngle(
                        new THREE.Vector3(0, 0, 1),
                        -Math.PI * 0.48 // Original PSD tilt
                    );
                    wrapper.quaternion.copy(layFlat.multiply(faceRotation).multiply(origQuat));
                    camera.position.set(0.0, 0.3, 1.5);
                    camera.lookAt(0, 0, 0);
                } else {
                    // PSD STANDING VIEW: Corner perspective
                    wrapper.quaternion.copy(faceRotation.multiply(origQuat));
                    camera.position.set(0.3, 1.5, 2.0);
                    camera.lookAt(0, -0.1, 0); 
                }
                
                // Update world matrices so bounding box calculation is accurate
                wrapper.updateMatrixWorld(true);
                camera.updateMatrixWorld(true);

                // --- RESOLUTION & CANVAS SETUP ---
                let capW: number, capH: number;
                let orthoW: number, orthoH: number;
                
                if (isProof && isArtworkOnly) {
                    // AUTO-FIT: Compute tight bounds of visible artwork meshes
                    const artworkBox = new THREE.Box3();
                    scene.traverse((child: any) => {
                        if (child.isMesh && child.visible) {
                            artworkBox.expandByObject(child);
                        }
                    });
                    
                    if (!artworkBox.isEmpty()) {
                        const artworkSize = artworkBox.getSize(new THREE.Vector3());
                        const artworkCenter = artworkBox.getCenter(new THREE.Vector3());
                        
                        const padding = 1.05; // 5% padding to prevent edge clipping
                        orthoW = artworkSize.x * padding;
                        orthoH = artworkSize.y * padding;
                        
                        // Set resolution proportional to artwork, with long edge = 2000px
                        const maxRes = 2000;
                        if (orthoW > orthoH) {
                            capW = maxRes;
                            capH = Math.round(maxRes * (orthoH / orthoW));
                        } else {
                            capH = maxRes;
                            capW = Math.round(maxRes * (orthoW / orthoH));
                        }
                        
                        // Point camera at artwork center
                        camera.position.set(artworkCenter.x, artworkCenter.y, 50);
                        camera.lookAt(artworkCenter.x, artworkCenter.y, 0);
                    } else {
                        // Fallback if no visible meshes
                        capW = 1200; capH = 360;
                        orthoH = 1.2;
                        orthoW = orthoH * (capW / capH);
                    }
                } else if (isProof) {
                    capW = 1200;
                    capH = 360;
                    orthoH = 1.2;
                    orthoW = orthoH * (capW / capH);
                } else {
                    // PSD: mix of aspects
                    capW = isHorizontalFrame ? 2000 : 500;
                    capH = isHorizontalFrame ? 600 : 1200;
                    orthoW = 0; orthoH = 0; // Not used for PSD
                }

                const tempCanvas = document.createElement('canvas');
                tempCanvas.width = capW;
                tempCanvas.height = capH;
                const tempCtx = tempCanvas.getContext('2d')!;

                // Resize renderer
                gl.setSize(capW, capH);
                gl.setPixelRatio(1);

                // --- PROJECTION SYSTEM ---
                if (isProof) {
                    // ORTHOGRAPHIC PROJECTION (for 2D Proof/Artwork look)
                    cam.projectionMatrix.makeOrthographic(
                        -orthoW / 2,
                        orthoW / 2,
                        orthoH / 2,
                        -orthoH / 2,
                        0.1,
                        100
                    );
                } else {
                    // PERSPECTIVE PROJECTION (for PSD Mockup)
                    cam.aspect = capW / capH;
                    cam.updateProjectionMatrix();
                }
                
                // Render
                gl.render(scene, camera);

                const glCanvas = gl.domElement;
                tempCtx.drawImage(glCanvas, 0, 0, glCanvas.width, glCanvas.height, 0, 0, capW, capH);

                captures.push(tempCanvas);
            }

            // ---- Restore original state ----
            wrapper.quaternion.copy(origQuat);
            camera.position.copy(origCameraPos);
            
            // Restore Perspective Parameters
            cam.fov = origFov;
            cam.aspect = origAspect;
            cam.near = origNear;
            cam.far = origFar;
            cam.updateProjectionMatrix(); 
 
            // Restore Store State
            if (forceWireframe !== origWireframe) {
                useConfiguratorStore.getState().setWireframeMode(origWireframe);
            }
  
            gl.setSize(origSize.x, origSize.y);
            gl.setPixelRatio(origPixelRatio);
  
            // Restore Tone Mapping
            gl.toneMapping = origToneMapping;
            gl.toneMappingExposure = origExposure;
  
            scene.background = origBackground;
            const bg = useConfiguratorStore.getState().sceneBackground;
            gl.setClearColor(bg, 1);
  
            camera.lookAt(0, 0, 0);

            // Restore hidden objects
            hiddenObjects.forEach(obj => {
                obj.visible = true;
            });

            gl.render(scene, camera);
  
            return captures;
        };

        return () => {
            window._sceneCaptureMultiAngle = undefined;
        };
    }, [gl, scene, camera]);

    return (
        <group ref={contentRef}>
             <ModelLoader />
        </group>
    );
};

export default function Scene() {
    const controlsRef = useRef<CameraControlsHandle>(null);
    const { isLoading, toolMode, glbUrl, currentModel, setLightingPreset, lightingPreset, sceneBackground } = useConfiguratorStore();

    // Reset camera when model changes
    useEffect(() => {
        if (controlsRef.current) {
            controlsRef.current.reset();
        }
        
        // Sync Default Lighting for Model
        const product = availableProducts.find(p => p.id === currentModel);
        if (product && product.defaultLighting) {
             setLightingPreset(product.defaultLighting);
        }
    }, [glbUrl, currentModel, setLightingPreset]);

    // Expose reset and download functions globally for toolbar
    if (typeof window !== 'undefined') {
        window._sceneReset = () => controlsRef.current?.reset();
        window._sceneDownload = () => controlsRef.current?.download();
        window._sceneZoomIn = () => controlsRef.current?.zoomIn();
        window._sceneZoomOut = () => controlsRef.current?.zoomOut();
        // _sceneRecordVideo is handled in SceneContent
    }
    
    // Removed local event listeners for HDRI - using Store now

    return (
        <div className="scene" data-tool={toolMode || 'select'}>
            {/* Loading progress bar for 3D view only */}
            {isLoading && <ModelLoadingBar />}

            <Canvas
                shadows={false} // Explicitly disabled - saves 50MB VRAM
                gl={{
                    preserveDrawingBuffer: true, // Required for export
                    antialias: true,
                    alpha: false, // Ensure solid background for video recording
                    toneMapping: THREE.ACESFilmicToneMapping, // Realistic tone mapping
                    toneMappingExposure: 0.9, // More subtle
                }}
                onCreated={({ gl }) => {
                    // Set clear color explicitly to be sure
                    gl.setClearColor(sceneBackground, 1);
                }}
            >
                <color attach="background" args={[sceneBackground]} />
                <PerspectiveCamera 
                    makeDefault 
                    position={[0, 0.5, typeof window !== 'undefined' && window.innerWidth < 768 ? 1.8 : 2.5]} 
                    fov={45} 
                />

                {/* Dynamic HDRI Environment - User selectable */}
                <Environment
                    preset={lightingPreset as any}
                    background={false}
                    environmentIntensity={0.6}
                    blur={0.8}
                />

                {/* Balanced Lighting - Good visuals + Good performance */}

                {/* Ambient light for base illumination - Low intensity to prevent washing out */}
                <ambientLight intensity={0.3} />

                {/* Key light - Main illumination causing form shadows */}
                <directionalLight
                    position={[5, 10, 7.5]}
                    intensity={1.2}
                    castShadow={false}
                />

                {/* Fill light - Soften shadows */}
                <directionalLight
                    position={[-3, 2, -3]}
                    intensity={0.4}
                />

                {/* Rim light - Highlight edges and separate from background */}
                <directionalLight
                    position={[0, 1, -5]}
                    intensity={1.0}
                    color="#dceeff"
                />

                {/* Top light for highlights */}
                <directionalLight
                    position={[0, 10, 0]}
                    intensity={0.5}
                />

                {/* Front Light - Critical for metallic reflections (Gold Foil) */}
                <directionalLight
                    position={[0, 0, 5]}
                    intensity={0.6}
                />

                {/* 3D Model */}
                <Suspense fallback={null}>
                    <SceneContent />
                </Suspense>

                {/* Camera controls with reset and download */}
                <CameraControls ref={controlsRef} />
            </Canvas>


        </div>
    );
}
